/**
 * Discrimination (PBI) bands — one source for every surface that reports how
 * well a question separates strong from weak examinees.
 *
 * The Mix tab used to cut at 0.40 / 0.25 while the composer inspector tinted its
 * meter at 0.30 / 0.20, so the same question could read "Adequate" in one place
 * and warning-red in the other. Bands live here; surfaces choose how to draw
 * them.
 *
 * PBI is the Point-Biserial Index: the correlation between getting this item
 * right and total score. Higher is better, and an item with no exam history has
 * no PBI at all — which is a different statement from a low one.
 */

export type PbiBandKey = "strong" | "adequate" | "weak" | "none"

export interface PbiBand {
  key: PbiBandKey
  /** Short name for chips, inspector rows, and legends. */
  label: string
  /** Label plus its numeric range, for distribution legends. */
  rangeLabel: string
  /** Meter / bar fill when a surface draws the score itself. */
  meterClassName: string
}

/** Ordered best → worst, then "never used". */
export const PBI_BANDS: readonly PbiBand[] = [
  {
    key: "strong",
    // Not "Strong" — next to the metric name "discrimination" that word reads
    // as a social harm, not as "this item ranks examinees well". Say what the
    // number means: how clearly right answers track with higher total scores.
    label: "Separates well",
    rangeLabel: "Separates well (0.40+)",
    meterClassName: "bg-brand-1",
  },
  {
    key: "adequate",
    label: "Separates somewhat",
    rangeLabel: "Separates somewhat (0.25 to 0.39)",
    meterClassName: "bg-brand-1/62",
  },
  {
    key: "weak",
    label: "Doesn't separate",
    rangeLabel: "Doesn't separate (below 0.25)",
    meterClassName: "bg-destructive",
  },
  {
    key: "none",
    label: "No history",
    rangeLabel: "No history",
    meterClassName: "bg-muted-foreground/25",
  },
] as const

const BY_KEY = new Map(PBI_BANDS.map((band) => [band.key, band]))

export const PBI_STRONG_MIN = 0.4
export const PBI_ADEQUATE_MIN = 0.25

/** `null`, `undefined`, `"--"`, and unparseable values all mean "never used". */
export function pbiBand(value: number | string | null | undefined): PbiBand {
  const numeric = typeof value === "string" ? Number(value) : value
  if (numeric === null || numeric === undefined || Number.isNaN(numeric)) {
    return BY_KEY.get("none")!
  }
  if (numeric >= PBI_STRONG_MIN) return BY_KEY.get("strong")!
  if (numeric >= PBI_ADEQUATE_MIN) return BY_KEY.get("adequate")!
  return BY_KEY.get("weak")!
}

/** Tooltip / hint copy — the same explanation everywhere PBI is shown. */
export const PBI_EXPLAINER =
  "Point-biserial index. How clearly getting this question right tracks with a high total score. 0.40 and above separates examinees well; below 0.25 means the item does not separate them."
